import React, { useEffect, useState } from 'react';
import { BellIcon, UserIcon, MessageSquareIcon, ClockIcon, EyeIcon, ToggleLeftIcon, ToggleRightIcon, CalendarIcon } from 'lucide-react';
import AccountabilityBuddySection from '../../components/AccountabilityBuddySection';
interface SupportBuddyPageProps {
  userType?: 'mentor' | 'mentee';
}
const SupportBuddyPage: React.FC<SupportBuddyPageProps> = ({
  userType = 'mentee'
}) => {
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [reminderFrequency, setReminderFrequency] = useState('biweekly');
  const [showSMSHistory, setShowSMSHistory] = useState(false);
  const SMSPreview = () => <div className="bg-gray-50 rounded-lg p-4" data-id="element-2053">
      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2054">
        Sample Reminder Preview
      </h4>
      <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-2055">
        <p className="text-sm text-gray-600" data-id="element-2056">
          "Hey! Your friend [Name] is halfway through their mentorship. Why not
          check in on how it's going? Reply STOP to opt out."
        </p>
      </div>
    </div>;
  return <div className="max-w-4xl mx-auto py-6 px-4" data-id="element-2057">
      <div className="mb-8" data-id="element-2058">
        <h1 className="text-2xl font-bold mb-2" data-id="element-2059">Support Buddy</h1>
        <p className="text-gray-600" data-id="element-2060">
          Manage your support network and reminder preferences.
        </p>
      </div>
      {/* Invite Section */}
      <div className="mb-8" data-id="element-2061">
        <AccountabilityBuddySection data-id="element-2062" />
      </div>
      {/* Quick Overview Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-id="element-2063">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2064">
          <div className="flex items-center" data-id="element-2065">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4" data-id="element-2066">
              <BellIcon className="text-purple-600" size={24} data-id="element-2067" />
            </div>
            <div data-id="element-2068">
              <p className="text-sm text-gray-500" data-id="element-2069">Reminder Status</p>
              <p className="font-medium" data-id="element-2070">
                {remindersEnabled ? 'Active' : 'Paused'}
              </p>
            </div>
          </div>
          <div className="flex items-center" data-id="element-2071">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4" data-id="element-2072">
              <MessageSquareIcon className="text-purple-600" size={24} data-id="element-2073" />
            </div>
            <div data-id="element-2074">
              <p className="text-sm text-gray-500" data-id="element-2075">Last Update</p>
              <button onClick={() => setShowSMSHistory(true)} className="text-purple-600 hover:text-purple-700 text-sm font-medium" data-id="element-2076">
                View History →
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Reminder Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-id="element-2077">
        <h3 className="text-lg font-semibold mb-4" data-id="element-2078">Reminder Settings</h3>
        <div className="space-y-6" data-id="element-2079">
          <div className="flex items-center justify-between" data-id="element-2080">
            <div className="flex items-center" data-id="element-2081">
              <ClockIcon className="text-gray-400 mr-2" size={20} data-id="element-2082" />
              <span className="font-medium" data-id="element-2083">Enable Reminders</span>
            </div>
            <button onClick={() => setRemindersEnabled(!remindersEnabled)} className="text-purple-600" data-id="element-2084">
              {remindersEnabled ? <ToggleRightIcon size={24} data-id="element-2085" /> : <ToggleLeftIcon size={24} data-id="element-2086" />}
            </button>
          </div>
          <div data-id="element-2087">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2088">
              Reminder Frequency
            </label>
            <select value={reminderFrequency} onChange={e => setReminderFrequency(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" disabled={!remindersEnabled} data-id="element-2089">
              <option value="weekly" data-id="element-2090">Weekly</option>
              <option value="biweekly" data-id="element-2091">Bi-weekly</option>
              <option value="monthly" data-id="element-2092">Monthly</option>
            </select>
          </div>
          <SMSPreview data-id="element-2093" />
        </div>
      </div>
      {/* Buddy Management Section */}
      <AccountabilityBuddySection data-id="element-2094" />
      {/* SMS History Modal */}
      {showSMSHistory && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-2095">
          <div className="bg-white rounded-xl w-full max-w-md p-6" data-id="element-2096">
            <div className="flex justify-between items-center mb-4" data-id="element-2097">
              <h3 className="text-lg font-semibold" data-id="element-2098">Message History</h3>
              <button onClick={() => setShowSMSHistory(false)} className="text-gray-400 hover:text-gray-600" data-id="element-2099">
                ×
              </button>
            </div>
            <div className="space-y-4" data-id="element-2100">
              {[{
            date: '2024-02-15',
            message: 'Monthly progress update sent to all buddies'
          }, {
            date: '2024-02-01',
            message: 'Goal achievement notification sent'
          }, {
            date: '2024-01-15',
            message: 'Monthly progress update sent to all buddies'
          }].map((item, index) => <div key={index} className="flex items-start border-b border-gray-100 pb-3" data-id="element-2101">
                  <CalendarIcon size={16} className="text-gray-400 mr-2 mt-1" data-id="element-2102" />
                  <div data-id="element-2103">
                    <p className="text-sm text-gray-600" data-id="element-2104">{item.message}</p>
                    <p className="text-xs text-gray-400" data-id="element-2105">{item.date}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}
    </div>;
};
export default SupportBuddyPage;